<script setup>
import { ref, onMounted, computed } from 'vue'
import { migrateLegacySubject, isLegacySubject } from '@/utils/unescoSubjects'
import { unescoSubjects } from '@/config/unescoSubjects'

const props = defineProps({
  modelValue: { type: [String, Array], default: null }
})

const emit = defineEmits(['update:modelValue'])

const selected = computed({
  get: () => {
    // Migrer automatiquement les anciennes valeurs vers UNESCO
    if (props.modelValue && isLegacySubject(props.modelValue)) {
      const migratedValue = migrateLegacySubject(props.modelValue)
      console.log(`🔄 Auto-migrating legacy subject: "${props.modelValue}" → "${migratedValue}"`)
      // Émettre la valeur migrée immédiatement pour mettre à jour le parent
      setTimeout(() => {
        emit('update:modelValue', migratedValue)
        // Afficher le badge de migration temporairement
        showMigrationBadge.value = true
        setTimeout(() => {
          showMigrationBadge.value = false
        }, 3000) // Masquer après 3 secondes
      }, 0)
      return migratedValue
    }
    return props.modelValue
  },
  set: (value) => emit('update:modelValue', value)
})

const rawSubjects = ref([])
const loading = ref(true)
const searchTerm = ref('')
const isOpen = ref(false)
const showMigrationBadge = ref(false)

onMounted(() => {
  // Charger les données depuis le module importé (plus fiable que fetch)
  rawSubjects.value = unescoSubjects
  loading.value = false
  console.log(`✅ Loaded ${rawSubjects.value.length} UNESCO subjects from module`)
})

// Construction de l'arborescence
const subjectOptions = computed(() => {
  if (!rawSubjects.value.length) return []
  
  const map = new Map()

  // Première passe : créer tous les nœuds
  rawSubjects.value.forEach(entry => {
    map.set(entry.code, {
      value: entry.code,
      label: entry.label,
      children: [],
      parent: entry.parent || null
    })
  })

  // Deuxième passe : construire la hiérarchie
  const root = []
  map.forEach((node) => {
    if (!node.parent) {
      root.push(node)
    } else {
      const parent = map.get(node.parent)
      if (parent) {
        parent.children.push(node)
      }
    }
  })

  // Transformer en format plat avec indentation
  const flattenWithIndent = (nodes, level = 0) => {
    let result = []
    nodes.forEach(node => {
      const indent = '  '.repeat(level)
      result.push({
        value: node.value,
        label: indent + node.label,
        searchLabel: node.label,
        level: level,
        hasChildren: node.children.length > 0,
        disabled: level === 0 && node.children.length > 0
      })
      if (node.children.length > 0) {
        result = result.concat(flattenWithIndent(node.children, level + 1))
      }
    })
    return result
  }

  return flattenWithIndent(root)
})

// Options filtrées selon la recherche
const filteredOptions = computed(() => {
  if (!searchTerm.value.trim()) return subjectOptions.value
  
  const search = searchTerm.value.toLowerCase()
  return subjectOptions.value.filter(option => {
    return option.searchLabel.toLowerCase().includes(search)
  })
})

// Libellé affiché pour l'option sélectionnée
const selectedLabel = computed(() => {
  if (!selected.value) return ''
  const option = subjectOptions.value.find(opt => opt.value === selected.value)
  return option ? option.searchLabel : selected.value
})

const selectOption = (option) => {
  if (!option.disabled) {
    selected.value = option.value
    isOpen.value = false
    searchTerm.value = ''
  }
}

const clearSelection = () => {
  selected.value = ''
  isOpen.value = false
  searchTerm.value = ''
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    searchTerm.value = ''
    // Focus sur l'input de recherche
    setTimeout(() => {
      const searchInput = document.querySelector('.subject-search-input')
      if (searchInput) searchInput.focus()
    }, 100)
  }
}

// Fermer le dropdown si on clique ailleurs
const handleClickOutside = (event) => {
  const selector = event.target.closest('.subject-selector')
  if (!selector) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

import { onUnmounted } from 'vue'
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="subject-selector">
    <!-- Input principal -->
    <div class="subject-input" @click="toggleDropdown">
      <input
        type="text"
        class="form-control"
        :value="selectedLabel"
        :placeholder="loading ? 'Loading...' : 'Choose a subject - UNESCO ISCED-F 2013'"
        readonly
        :disabled="loading"
      />
      <span class="dropdown-arrow">
        <i :class="isOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
      </span>
      <button 
        v-if="selected" 
        type="button" 
        class="clear-btn"
        @click.stop="clearSelection"
        title="Clear selection"
      >
        <i class="fas fa-times"></i>
      </button>
      
      <!-- Badge de migration -->
      <div v-if="showMigrationBadge" class="migration-badge">
        <i class="fas fa-sync-alt"></i> Migrated to UNESCO
      </div>
    </div>

    <!-- Dropdown avec recherche -->
    <div v-show="isOpen" class="subject-dropdown">
      <!-- Input de recherche -->
      <div class="search-container">
        <input
          v-model="searchTerm"
          type="text"
          class="form-control subject-search-input"
          placeholder="Search subjects..."
        />
        <i class="fas fa-search search-icon"></i>
      </div>

      <!-- Liste des options -->
      <div class="options-container">
        <div v-if="filteredOptions.length === 0" class="no-results">
          No results found
        </div>
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="option"
          :class="{
            'option-selected': option.value === selected,
            'option-disabled': option.disabled,
            'option-category': option.level === 0 && option.hasChildren
          }"
          @click="selectOption(option)"
        >
          {{ option.label }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.subject-selector {
  position: relative;
  width: 100%;
}

.subject-input {
  position: relative;
  cursor: pointer;
}

.subject-input input {
  cursor: pointer;
  padding-right: 60px; /* Space for arrow and clear button */
  min-height: 56px; /* Match Bootstrap floating labels */
}

.dropdown-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
}

.clear-btn {
  position: absolute;
  right: 35px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  font-size: 14px;
  padding: 4px;
  cursor: pointer;
  border-radius: 3px;
}

.clear-btn:hover {
  color: #dc3545;
  background-color: #f8f9fa;
}

.subject-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
}

.search-container {
  position: relative;
  padding: 8px;
  border-bottom: 1px solid #dee2e6;
}

.search-container input {
  padding-right: 30px;
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
}

.options-container {
  max-height: 240px;
  overflow-y: auto;
}

.option {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.15s ease;
}

.option:hover:not(.option-disabled) {
  background-color: #e9ecef;
}

.option-selected {
  background-color: #0d6efd;
  color: white;
}

.option-disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  font-weight: 600;
}

.option-category {
  font-weight: 600;
  background-color: #f8f9fa;
}

.no-results {
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.migration-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #28a745;
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  white-space: nowrap;
  z-index: 1001;
  animation: fadeInOut 3s ease-in-out;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: scale(0.8); }
  20% { opacity: 1; transform: scale(1); }
  80% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.8); }
}

/* Responsive */
@media (max-width: 576px) {
  .subject-dropdown {
    max-height: 200px;
  }
  
  .options-container {
    max-height: 140px;
  }
}
</style>